import LocalStorageManager from '@/lib/utils/localStorageManager'
import { getNumericCookie, getNumericCookieFromString, setNumericCookie } from '@/utils/cookies'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface LayoutSetting {
  customizationPanelDisplayedBefore?: boolean
  preferredFirstPanelWidth?: number
}

// Cookie name for panel width preferences
const PANEL_WIDTH_COOKIE_KEY = 'panel_width'

export const updateLayoutSetting = (setting: Partial<LayoutSetting>): void => {
  const existingSetting = getLayoutSetting()
  const updatedSetting = { ...existingSetting, ...setting }
  LocalStorageManager.setItem(`layoutSetting`, JSON.stringify(updatedSetting))
}

export const getLayoutSetting = (): LayoutSetting => {
  const storedSetting = LocalStorageManager.getItem(`layoutSetting`)
  return storedSetting ? JSON.parse(storedSetting) : {}
}

export const updatePreferredFirstPanelWidth = (width: number): void => {
  setNumericCookie(PANEL_WIDTH_COOKIE_KEY, width)
}

export const getPreferredFirstPanelWidth = (): number | undefined => {
  return getNumericCookie(PANEL_WIDTH_COOKIE_KEY)
}

// Server-side compatible function to get panel width from cookie string
export const getPreferredFirstPanelWidthFromCookieString = (
  cookieString: string
): number | undefined => {
  return getNumericCookieFromString(cookieString, PANEL_WIDTH_COOKIE_KEY)
}

const SIDEBAR_COLLAPSED_COOKIE_KEY = 'sidebar_collapsed'

export const updateSidebarCollapsed = (collapsed: boolean): void => {
  setNumericCookie(SIDEBAR_COLLAPSED_COOKIE_KEY, collapsed ? 1 : 0)
}

export const getSidebarCollapsed = (): boolean => {
  const value = getNumericCookie(SIDEBAR_COLLAPSED_COOKIE_KEY)
  return value === 1
}

export const getSidebarCollapsedFromCookieString = (cookieString: string): boolean => {
  const value = getNumericCookieFromString(cookieString, SIDEBAR_COLLAPSED_COOKIE_KEY)
  return value === 1
}

interface LayoutState {
  showAppPanel: boolean
  showCustomizationPanel: boolean
  showCustomizationNudge: boolean
  minimizeCustomizationPanel: boolean
  showErrorWhileGeneration: boolean
  errorMessage: string | null
  canShowEmptyLogo: boolean
  canShowEmptySeeds: boolean
}

export const initialState: LayoutState = {
  showAppPanel: false,
  showCustomizationPanel: false,
  showCustomizationNudge: false,
  minimizeCustomizationPanel: false,
  showErrorWhileGeneration: false,
  errorMessage: null,
  canShowEmptyLogo: false,
  canShowEmptySeeds: false,
}

const layoutSlice = createSlice({
  name: 'layout',
  initialState,
  reducers: {
    setShowAppPanel: (state: LayoutState, action: PayloadAction<boolean>) => {
      state.showAppPanel = action.payload
    },
    setShowCustomizationPanel: (state: LayoutState, action: PayloadAction<boolean>) => {
      state.showCustomizationPanel = action.payload
    },
    setShowCustomizationNudge: (state: LayoutState, action: PayloadAction<boolean>) => {
      state.showCustomizationNudge = action.payload
    },
    setShowErrorWhileGeneration: (state: LayoutState, action: PayloadAction<boolean>) => {
      state.showErrorWhileGeneration = action.payload
      if (!action.payload) {
        state.errorMessage = null
      }
    },
    setErrorMessage: (state: LayoutState, action: PayloadAction<string | null>) => {
      state.errorMessage = action.payload
    },
    setCanShowEmptyLogo: (state: LayoutState, action: PayloadAction<boolean>) => {
      state.canShowEmptyLogo = action.payload
    },
    setCanShowEmptySeeds: (state: LayoutState, action: PayloadAction<boolean>) => {
      state.canShowEmptySeeds = action.payload
    },
    setMinimizeCustomizationPanel: (state: LayoutState, action: PayloadAction<boolean>) => {
      state.minimizeCustomizationPanel = action.payload
    },
  },
})

export const {
  setShowAppPanel,
  setShowCustomizationPanel,
  setShowCustomizationNudge,
  setMinimizeCustomizationPanel,
  setShowErrorWhileGeneration,
  setErrorMessage,
  setCanShowEmptyLogo,
  setCanShowEmptySeeds,
} = layoutSlice.actions

export default layoutSlice.reducer
